import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { useNumberFormat } from "@/components/NumberFormat/hooks/useNumberFormat";
import { Tooltip } from "@/components/Tooltip";
import { Trans } from "@/features/i18n/Trans";
import { getTimezoneOffset } from "@/features/timezone/timezone";
import { useDateFormat } from "@/hooks/useDateFormat";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { RootQueriesContext } from "@/queriesContext/rootQueriesContext/context";

type EmailChannelStatus = InterludeTypes["EmailChannel"]["status"];
type BannerVariant = "error" | "warning";

interface DomainHealthVisual {
  label: React.ReactNode;
  icon: React.ReactNode;
  banner: {
    variant: BannerVariant;
    title: React.ReactNode;
    description: React.ReactNode;
    learnMoreHref: string;
  } | null;
  statusTooltip: {
    title: React.ReactNode;
    description: React.ReactNode;
    learnMoreHref: string;
  } | null;
}

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    pt: "8px",
  },
  sectionTitle: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.colors.staticFgTitle,
  },
  domainHealthSection: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    maxWidth: "900px",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  statusIcon: {
    width: "20px",
    height: "20px",
    flexShrink: 0,
    display: "flex",
    fontSize: "20px",
  },
  statusLabel: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: "24px",
    color: theme.colors.staticFgBody,
  },
  statusInfoIcon: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    display: "flex",
    fontSize: "16px",
    color: theme.colors.staticFgNote,
    ml: "4px",
  },
  banner: {
    width: "100%",
    maxWidth: "900px",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
  metricsCard: {
    display: "flex",
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    py: "16px",
    width: "100%",
  },
  metricCell: {
    flex: "1 1 0",
    minWidth: 0,
    px: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    "&:not(:last-of-type)": {
      borderRight: `1px solid ${theme.colors.staticFgLine}`,
    },
  },
  metricHeader: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  metricTitle: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "20px",
    color: theme.colors.staticFgTitle,
  },
  metricInfoIcon: {
    width: "16px",
    height: "16px",
    fontSize: "16px",
    color: theme.colors.staticFgNote,
    display: "flex",
  },
  metricValue: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "2.25rem",
    fontWeight: 400,
    lineHeight: "45px",
    color: theme.colors.staticFgBody,
  },
  tooltipBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "276px",
  },
  tooltipText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  tooltipTitle: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.colors.staticFgTitle,
  },
  tooltipDescription: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "20px",
    color: theme.colors.staticFgBody,
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
  usageSection: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    maxWidth: "900px",
  },
  usageCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "8px",
    px: "32px",
    py: "24px",
    width: "100%",
  },
  usageInner: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  usageRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontFamily: "Roboto, sans-serif",
    fontSize: "1rem",
    lineHeight: "24px",
    color: theme.colors.staticFgBody,
    fontWeight: 400,
  },
  progress: {
    width: "100%",
    height: "6px",
    borderRadius: "3px",
    backgroundColor: theme.colors.staticFgLine,
    "& .MuiLinearProgress-bar": {
      backgroundColor: theme.colors.colorBlue60,
      borderRadius: "3px",
    },
  },
  metricsUpdatedAt: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.75rem",
    lineHeight: "16px",
    color: theme.colors.staticFgNote,
    textAlign: "right",
    width: "100%",
  },
  resetNote: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.75rem",
    lineHeight: "16px",
    color: theme.colors.staticFgNote,
    textAlign: "right",
    width: "100%",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const formatSendingSpeed = (sendingSpeed: number | null): string => {
  if (sendingSpeed === null) return "—";
  return `${Math.round(sendingSpeed)}%`;
};

interface TooltipBodyProps {
  title?: React.ReactNode;
  description: React.ReactNode;
  learnMoreHref: string;
}

const TooltipBody: React.FC<TooltipBodyProps> = function TooltipBody({
  title,
  description,
  learnMoreHref,
}) {
  const { t } = useTranslation();
  return (
    <Box sx={styles.tooltipBody}>
      <Box sx={styles.tooltipText}>
        {title ? <Box sx={styles.tooltipTitle}>{title}</Box> : null}
        <Box sx={styles.tooltipDescription}>{description}</Box>
      </Box>
      <ExternalLink href={learnMoreHref} trailingIcon={true}>
        {t("common.learnMore")}
      </ExternalLink>
    </Box>
  );
};

const getDomainHealthVisual = (
  status: EmailChannelStatus,
  senderDomain: string,
  t: ReturnType<typeof useTranslation>["t"],
): DomainHealthVisual | null => {
  switch (status) {
    case "suspended":
      return {
        label: (
          <Trans i18nKey="channels.email.detail.health.status.suspended" />
        ),
        icon: (
          <MotifIcon
            un-i-motif="circle_caution_filled"
            style={{ color: theme.colors.staticFgNegative }}
          />
        ),
        banner: {
          variant: "error",
          title: (
            <Trans i18nKey="channels.email.detail.health.banner.suspended.title" />
          ),
          description: (
            <Trans
              i18nKey="channels.email.detail.health.banner.suspended.description"
              values={{ domain: senderDomain }}
            />
          ),
          learnMoreHref: t(
            "channels.email.detail.health.banner.suspended.learnMoreUrl",
          ),
        },
        statusTooltip: null,
      };
    case "warning":
      return {
        label: <Trans i18nKey="channels.email.detail.health.status.warning" />,
        icon: (
          <MotifIcon
            un-i-motif="warning"
            style={{ color: theme.colors.staticFgAttentive }}
          />
        ),
        banner: {
          variant: "warning",
          title: (
            <Trans i18nKey="channels.email.detail.health.banner.warning.title" />
          ),
          description: (
            <Trans
              i18nKey="channels.email.detail.health.banner.warning.description"
              values={{ domain: senderDomain }}
            />
          ),
          learnMoreHref: t(
            "channels.email.detail.health.banner.warning.learnMoreUrl",
          ),
        },
        statusTooltip: null,
      };
    case "restricted":
      return {
        label: (
          <Trans i18nKey="channels.email.detail.health.status.restricted" />
        ),
        icon: (
          <MotifIcon
            un-i-motif="warning"
            style={{ color: theme.colors.staticFgAttentive }}
          />
        ),
        banner: {
          variant: "warning",
          title: (
            <Trans i18nKey="channels.email.detail.health.banner.restricted.title" />
          ),
          description: (
            <Trans
              i18nKey="channels.email.detail.health.banner.restricted.description"
              values={{ domain: senderDomain }}
            />
          ),
          learnMoreHref: t(
            "channels.email.detail.health.banner.restricted.learnMoreUrl",
          ),
        },
        statusTooltip: null,
      };
    case "building_reputation":
      return {
        label: (
          <Trans i18nKey="channels.email.detail.health.status.buildingReputation" />
        ),
        icon: (
          <MotifIcon
            un-i-motif="circle_check_filled"
            style={{ color: theme.colors.staticFgPrimary }}
          />
        ),
        banner: null,
        statusTooltip: {
          title: (
            <Trans i18nKey="channels.email.detail.health.status.buildingReputation.tooltip.title" />
          ),
          description: (
            <Trans
              i18nKey="channels.email.detail.health.status.buildingReputation.tooltip.description"
              values={{ domain: senderDomain }}
            />
          ),
          learnMoreHref: t(
            "channels.email.detail.health.status.buildingReputation.tooltip.learnMoreUrl",
          ),
        },
      };
    case "healthy":
    case "connected": // Connected status is currently treated the same as Healthy in the UI, but it will be removed in the future
      return {
        label: <Trans i18nKey="channels.email.detail.health.status.healthy" />,
        icon: (
          <MotifIcon
            un-i-motif="circle_check_filled"
            style={{ color: theme.colors.staticFgPositive }}
          />
        ),
        banner: null,
        statusTooltip: null,
      };
    default:
      // `disconnected` is the initial state for self-onboarded email
      // channels; treated the same as `not_set` in the sending-status UI
      // since neither has begun warming up.
      status satisfies "not_set" | "disconnected";
      return null;
  }
};

const getStatusLabelString = (
  status: EmailChannelStatus,
  t: ReturnType<typeof useTranslation>["t"],
): string => {
  switch (status) {
    case "suspended":
      return t("channels.email.detail.health.status.suspended");
    case "restricted":
      return t("channels.email.detail.health.status.restricted");
    case "warning":
      return t("channels.email.detail.health.status.warning");
    case "building_reputation":
      return t("channels.email.detail.health.status.buildingReputation");
    case "healthy":
    case "connected": // Connected status is currently treated the same as Healthy in the UI, but it will be removed in the future
      return t("channels.email.detail.health.status.healthy");
    default:
      // `disconnected` is the initial state for self-onboarded email
      // channels; treated the same as `not_set` in the sending-status UI
      // since neither has begun warming up.
      status satisfies "not_set" | "disconnected";
      return "";
  }
};

interface MetricCellProps {
  title: React.ReactNode;
  tooltip: React.ReactNode;
  value: React.ReactNode;
}

const MetricCell: React.FC<MetricCellProps> = function MetricCell({
  title,
  tooltip,
  value,
}) {
  return (
    <Box sx={styles.metricCell}>
      <Box sx={styles.metricHeader}>
        <Box sx={styles.metricTitle}>{title}</Box>
        <Tooltip title={tooltip} placement="top">
          <Box component="span" sx={styles.metricInfoIcon}>
            <MotifIcon un-i-motif="circle_info" />
          </Box>
        </Tooltip>
      </Box>
      <Box sx={styles.metricValue}>{value}</Box>
    </Box>
  );
};

interface EmailSendingStatusTabProps {
  channelId: number;
  channelStatus: EmailChannelStatus;
  senderDomain: string;
}

export const EmailSendingStatusTab: React.FC<EmailSendingStatusTabProps> =
  function EmailSendingStatusTab({ channelId, channelStatus, senderDomain }) {
    const { t } = useTranslation();
    const { me } = RootQueriesContext.useData();
    const orgTimezone = me.organization.timezone;
    const healthMetricsQuery = interlude.channel.useGetEmailHealthMetrics({
      params: { channelId },
    });
    const sendingUsageQuery = interlude.channel.useGetEmailSendingUsage({
      params: { channelId },
    });

    const countFormat = useNumberFormat({ numberFormatPreset: "count" });
    const rateFormat = useNumberFormat({
      numberFormatPreset: "percent",
      numberFormatOptions: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      },
    });

    // Shown in the org timezone so the time lines up with the reset note.
    // An unhealthy channel keeps the window that set its status, so this can
    // be days old; without it the numbers read as current.
    const updatedAtFormatOptions = useMemo<Intl.DateTimeFormatOptions>(
      () => ({
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        ...(orgTimezone ? { timeZone: orgTimezone } : {}),
      }),
      [orgTimezone],
    );
    const formatUpdatedAt = useDateFormat({
      dateFormatOptions: updatedAtFormatOptions,
    });

    const stats = healthMetricsQuery.data;
    const usage = sendingUsageQuery.data;

    const visual = getDomainHealthVisual(channelStatus, senderDomain, t);
    const statusLabelText = getStatusLabelString(channelStatus, t);
    const sendingSpeedNumber = stats?.sendingSpeed ?? null;
    const sendingSpeedDisplay = formatSendingSpeed(sendingSpeedNumber);
    const sendingSpeedForTooltip =
      sendingSpeedNumber === null ? 0 : Math.round(sendingSpeedNumber);

    const bounceCount = stats?.bounceCount ?? 0;
    const spamCount = stats?.spamCount ?? 0;
    const totalSent = stats?.totalSent ?? 0;
    const bounceRate = totalSent > 0 ? bounceCount / totalSent : 0;
    const spamRate = totalSent > 0 ? spamCount / totalSent : 0;
    const bounceValue = `${countFormat.numberFormat(bounceCount)} (${rateFormat.numberFormat(bounceRate)})`;
    const spamValue = `${countFormat.numberFormat(spamCount)} (${rateFormat.numberFormat(spamRate)})`;

    const usageCount = usage?.usage ?? 0;
    const capacityCount = usage?.capacity ?? 0;
    const usagePercent =
      capacityCount > 0 ? Math.min(100, (usageCount / capacityCount) * 100) : 0;

    return (
      <Box sx={styles.root}>
        <Box sx={styles.domainHealthSection}>
          <Box sx={styles.sectionTitle}>
            <Trans i18nKey="channels.email.detail.health.title" />
          </Box>
          {visual ? (
            <Box sx={styles.statusRow}>
              <Box sx={styles.statusIcon}>{visual.icon}</Box>
              <Box sx={styles.statusLabel}>{visual.label}</Box>
              {visual.statusTooltip ? (
                <Tooltip
                  title={
                    <TooltipBody
                      title={visual.statusTooltip.title}
                      description={visual.statusTooltip.description}
                      learnMoreHref={visual.statusTooltip.learnMoreHref}
                    />
                  }
                  placement="bottom-start"
                >
                  <Box component="span" sx={styles.statusInfoIcon}>
                    <MotifIcon un-i-motif="circle_info" />
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          ) : null}
          {visual?.banner ? (
            <InfoBox
              variant={visual.banner.variant}
              title={visual.banner.title}
              description={visual.banner.description}
              href={visual.banner.learnMoreHref}
              sx={styles.banner}
            />
          ) : null}
          <Box sx={styles.metricsCard}>
            <MetricCell
              title={
                <Trans i18nKey="channels.email.detail.health.metric.sendingSpeed" />
              }
              tooltip={
                <TooltipBody
                  description={
                    <Trans
                      i18nKey="channels.email.detail.health.metric.sendingSpeed.tooltip"
                      values={{
                        status: statusLabelText || "Healthy",
                        value: sendingSpeedForTooltip,
                      }}
                    />
                  }
                  learnMoreHref={t(
                    "channels.email.detail.health.metric.sendingSpeed.tooltip.learnMoreUrl",
                  )}
                />
              }
              value={sendingSpeedDisplay}
            />
            <MetricCell
              title={
                <Trans i18nKey="channels.email.detail.health.metric.hardBounce" />
              }
              tooltip={
                <TooltipBody
                  description={
                    <Trans i18nKey="channels.email.detail.health.metric.hardBounce.tooltip" />
                  }
                  learnMoreHref={t(
                    "channels.email.detail.health.metric.hardBounce.tooltip.learnMoreUrl",
                  )}
                />
              }
              value={bounceValue}
            />
            <MetricCell
              title={
                <Trans i18nKey="channels.email.detail.health.metric.spam" />
              }
              tooltip={
                <TooltipBody
                  description={
                    <Trans i18nKey="channels.email.detail.health.metric.spam.tooltip" />
                  }
                  learnMoreHref={t(
                    "channels.email.detail.health.metric.spam.tooltip.learnMoreUrl",
                  )}
                />
              }
              value={spamValue}
            />
          </Box>
          {stats?.updatedAt ? (
            <Box sx={styles.metricsUpdatedAt}>
              {t(
                "channels.email.detail.health.metric.updatedAt",
                "Shows data from the most recent 8-hour period with at least 1,000 emails sent. Data is not updated in real time. Last updated: {{datetime}} ({{timezone}})",
                {
                  datetime: formatUpdatedAt(stats.updatedAt),
                  timezone: orgTimezone ? getTimezoneOffset(orgTimezone) : "",
                },
              )}
            </Box>
          ) : null}
        </Box>

        <Box sx={styles.usageSection}>
          <Box sx={styles.sectionTitle}>
            <Trans i18nKey="channels.email.detail.usage.title" />
          </Box>
          <Box sx={styles.usageCard}>
            <Box sx={styles.usageInner}>
              <Box sx={styles.usageRow}>
                <Box component="span">
                  <Trans
                    i18nKey="channels.email.detail.usage.used"
                    values={{ used: countFormat.numberFormat(usageCount) }}
                  />
                </Box>
                <Box component="span">
                  <Trans
                    i18nKey="channels.email.detail.usage.perDay"
                    values={{
                      capacity: countFormat.numberFormat(capacityCount),
                    }}
                  />
                </Box>
              </Box>
              <Stack
                direction="row"
                sx={{ alignItems: "center", width: "100%" }}
              >
                <LinearProgress
                  variant="determinate"
                  value={usagePercent}
                  sx={styles.progress}
                />
              </Stack>
            </Box>
            <Box sx={styles.resetNote}>
              <Trans
                i18nKey="channels.email.detail.usage.resetNote"
                values={{
                  timezone: orgTimezone ? getTimezoneOffset(orgTimezone) : "",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };
